import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, BookOpen, CalendarCheck, Info } from "lucide-react";
import Breadcrumbs from "@/components/Breadcrumbs";
import SEOHead from "@/components/SEOHead";
import GuideCopy, { GuideLink } from "@/components/GuideCopy";
import NotFoundPage from "@/pages/NotFoundPage";
import { getGuide, formatGuideDate } from "@/data/guidesContent";
import { buildGuidePageSchema } from "@/lib/schema/guidePageSchema";
import { fadeUp, stagger } from "@/utils/motionVariants";

/**
 * /guides/:guide — permanent comparison-guide template (handover
 * "AI-Search-Flagship" v02, Sep 2026). Not a blog: no dated URL, no feed,
 * Article (not BlogPosting) schema.
 *
 * Fixed section order for every guide, so later guides stay consistent:
 *   hero (breadcrumbs, title, reviewed date) → intro → disclosure →
 *   content sections (tables, provider notes, lists, FAQs) → closing CTA.
 *
 * All wording comes from data/guidesContent.js. Every link reports
 * guide_link_click with the section's cta_position (see GuideCopy).
 */

const PROSE = "text-base text-brand-grey-dark leading-relaxed";

function GuideTable({ block, guideId, position }) {
  const align = (i) => (block.align?.[i] === "right" ? "text-right" : "text-left");
  return (
    <div
      className="my-6 overflow-x-auto rounded-2xl border border-brand-light-grey focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-aqua"
      role="region"
      aria-label={block.caption}
      tabIndex={0}
    >
      <table className="w-full min-w-[36rem] text-sm border-collapse">
        <caption className="sr-only">{block.caption}</caption>
        <thead className="bg-gray-50">
          <tr>
            {block.columns.map((c, i) => (
              <th key={c} scope="col" className={`px-4 py-3 font-semibold text-brand-black border-b border-brand-light-grey align-bottom ${align(i)}`}>
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {block.rows.map((row) => (
            <tr key={row[0]} className="border-b border-brand-light-grey last:border-b-0 align-top">
              {row.map((cell, i) =>
                i === 0 && block.rowHeaders ? (
                  <th key={block.columns[i]} scope="row" className={`px-4 py-3 font-semibold text-brand-black whitespace-nowrap ${align(i)}`}>
                    <GuideCopy text={cell} guideId={guideId} position={position} />
                  </th>
                ) : (
                  <td key={block.columns[i]} className={`px-4 py-3 text-brand-grey-dark leading-relaxed ${align(i)}`}>
                    <GuideCopy text={cell} guideId={guideId} position={position} />
                  </td>
                ),
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GuideBlock({ block, guideId, position }) {
  const copy = (text) => <GuideCopy text={text} guideId={guideId} position={position} />;
  switch (block.type) {
    case "h3":
      return <h3 className="font-heading text-xl font-bold text-brand-black mt-10 mb-3">{block.text}</h3>;
    case "p":
      return <p className={`${PROSE} mb-4`}>{copy(block.text)}</p>;
    case "ul":
      return (
        <ul className={`${PROSE} list-disc pl-6 mb-4 space-y-1.5`}>
          {block.items.map((item) => (<li key={item}>{copy(item)}</li>))}
        </ul>
      );
    case "ol":
      return (
        <ol className={`${PROSE} list-decimal pl-6 mb-4 space-y-2`}>
          {block.items.map((item) => (<li key={item}>{copy(item)}</li>))}
        </ol>
      );
    case "table":
      return <GuideTable block={block} guideId={guideId} position={position} />;
    default:
      return null;
  }
}

export default function GuidePage() {
  const { guide: slug } = useParams();
  const guide = getGuide(slug);
  if (!guide) return <NotFoundPage />;

  const { guideId, closing } = guide;
  const reviewed = formatGuideDate(guide.reviewed);

  return (
    <div data-testid="guide-page" data-guide-id={guideId}>
      <SEOHead
        {...(guide.seoTitle ? { rawTitle: guide.seoTitle } : { title: guide.h1 })}
        description={guide.metaDescription}
        path={guide.path}
        schema={buildGuidePageSchema(guide, guide.metaDescription)}
      />

      {/* Hero */}
      <section className="hero-gradient py-14 md:py-20" data-testid="guide-hero">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <Breadcrumbs className="mb-5" />
            <motion.p
              variants={fadeUp}
              className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.25em] text-brand-aqua-dark mb-4"
            >
              <BookOpen size={12} aria-hidden="true" />
              {guide.eyebrow}
            </motion.p>
            <motion.h1
              variants={fadeUp}
              className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-brand-black tracking-tight leading-tight mb-5"
            >
              {guide.h1}
            </motion.h1>
            {reviewed && (
              <motion.p variants={fadeUp} className="inline-flex items-center gap-1.5 text-sm text-brand-grey" data-testid="guide-reviewed">
                <CalendarCheck size={14} aria-hidden="true" />
                <time dateTime={guide.reviewed}>{`Last reviewed ${reviewed}`}</time>
              </motion.p>
            )}
          </motion.div>
        </div>
      </section>

      <article className="py-12 md:py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Intro + disclosure */}
          <div data-testid="guide-intro">
            {guide.intro.map((p) => (
              <p key={p} className="text-lg text-brand-grey-dark leading-relaxed mb-4">{p}</p>
            ))}
          </div>
          {guide.disclosure && (
            <aside
              className="mt-6 mb-4 flex gap-3 rounded-2xl border border-brand-light-grey bg-gray-50 p-5"
              aria-label="About this guide"
              data-testid="guide-disclosure"
            >
              <Info size={18} className="text-brand-aqua-deep flex-shrink-0 mt-0.5" aria-hidden="true" />
              <p className="text-sm text-brand-grey-dark leading-relaxed">{guide.disclosure}</p>
            </aside>
          )}

          {/* Content sections */}
          {guide.sections.map((section) => (
            <section key={section.id} aria-labelledby={`guide-${section.id}`} className="mt-12" data-testid={`guide-section-${section.id}`}>
              <h2 id={`guide-${section.id}`} className="font-heading text-2xl sm:text-3xl font-bold text-brand-black tracking-tight mb-5 scroll-mt-24">
                {section.heading}
              </h2>
              {section.blocks.map((block) => (
                <GuideBlock key={block.text || block.caption || block.items?.[0]} block={block} guideId={guideId} position={section.position} />
              ))}
            </section>
          ))}
        </div>
      </article>

      {/* Closing CTA */}
      <section className="py-16 md:py-20 bg-brand-black text-white" aria-labelledby={`guide-${closing.id}`} data-testid="guide-closing">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 id={`guide-${closing.id}`} className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mb-4">{closing.heading}</h2>
          {(Array.isArray(closing.body) ? closing.body : [closing.body]).map((p, i, all) => (
            <p key={p} className={`text-white/80 leading-relaxed ${i === all.length - 1 ? "mb-8" : "mb-4"}`}>{p}</p>
          ))}
          <GuideLink
            href={closing.primary.to}
            guideId={guideId}
            position={closing.position}
            className="bg-brand-aqua-deep hover:bg-brand-aqua-deeper text-white font-semibold rounded-full px-7 py-3.5 text-sm inline-flex items-center justify-center gap-2 transition-colors btn-shine"
            testId="guide-closing-primary"
          >
            {closing.primary.label}
            <ArrowRight size={16} aria-hidden="true" />
          </GuideLink>
          <p className="mt-6 text-sm text-white/80 leading-relaxed [&_a]:text-white [&_a:hover]:text-brand-aqua [&_strong]:text-white" data-testid="guide-closing-secondary">
            <GuideCopy text={closing.secondary} guideId={guideId} position={closing.position} />
          </p>
          {closing.footnote && (
            <p className="mt-8 text-xs italic text-white/60 leading-relaxed" data-testid="guide-closing-footnote">{closing.footnote}</p>
          )}
        </div>
      </section>
    </div>
  );
}
